import { OperationStorage } from '../constants/operations';
import { isValidStorageValue } from './operationsGuards';

const isIpAddress = (hostname: string): boolean => /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);

const getCookieDomain = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;

  const { hostname } = window.location;
  if (!hostname || hostname === 'localhost' || isIpAddress(hostname)) return undefined;

  const hostnameParts = hostname.split('.');
  if (hostnameParts.length < 2) return undefined;

  return `.${hostnameParts.slice(-2).join('.')}`;
};

const getCookieValue = (key: OperationStorage): string | undefined => {
  if (typeof document === 'undefined') return undefined;

  const encodedKey = encodeURIComponent(key);
  const cookiePart = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${encodedKey}=`));

  if (!cookiePart) return undefined;
  const decodedValue = decodeURIComponent(cookiePart.slice(encodedKey.length + 1));
  return decodedValue || undefined;
};

const setDomainCookie = (key: OperationStorage, value: string): void => {
  if (typeof document === 'undefined') return;

  const domain = getCookieDomain();
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  const domainPart = domain ? `; Domain=${domain}` : '';
  document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; Path=/; SameSite=Lax${domainPart}${secure}`;
};

const removeDomainCookie = (key: OperationStorage): void => {
  if (typeof document === 'undefined') return;

  const domain = getCookieDomain();
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  const domainPart = domain ? `; Domain=${domain}` : '';
  const base = `${encodeURIComponent(key)}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;

  document.cookie = `${base}${domainPart}`;
  document.cookie = base;
};

export const setOperationStorageItem = (key: OperationStorage, value: string | null | undefined): void => {
  // Never persist falsy/undefined values — writing them corrupts the stored
  // selection and can produce `[undefined]`/`"[null]"` on the next read.
  if (!isValidStorageValue(value)) return;

  sessionStorage.setItem(key, value);
  setDomainCookie(key, value);
};

export const getOperationStorageItem = (key: OperationStorage): string | undefined => {
  const sessionStorageValue = sessionStorage.getItem(key);
  if (sessionStorageValue) return sessionStorageValue;

  const cookieValue = getCookieValue(key);
  if (cookieValue) {
    sessionStorage.setItem(key, cookieValue);
  }

  return cookieValue;
};

export const removeOperationStorageItem = (key: OperationStorage): void => {
  sessionStorage.removeItem(key);
  removeDomainCookie(key);
};
